import React, { useEffect, useState } from 'react';
import { Checkbox } from '@atlaskit/checkbox';
import Button from '@atlaskit/button';
import Badge from '@atlaskit/badge';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import './custom-filter-wrapper.scss';

interface CustomFilterCheckbox {
  filterField: string;
  filterableItems: Array<{ key: string; value: string }>;
  queryKey: string;
  updateQueryFilter: (filter: []) => void;
  shouldClearSelectedField: boolean;
  defaultSelectedFields?: string[];
  version?: number;
}

const CustomFilterCheckbox: React.FC<CustomFilterCheckbox> = ({
  filterField,
  filterableItems,
  queryKey,
  updateQueryFilter,
  shouldClearSelectedField,
  defaultSelectedFields,
  version,
}) => {
  const [selectedFields, setSelectedFields] = useState<string[]>(
    version ? (shouldClearSelectedField ? [] : defaultSelectedFields ?? []) : []
  );

  const updateParentFilter = (filters: string[]) => {
    const finalFilters: any = [];
    // Format the filters appropriately. Eg.: { status: "PAID" }
    filters.forEach((selectedField) => finalFilters.push({ [queryKey]: selectedField }));

    updateQueryFilter(finalFilters);
  };

  const handleSelect = (value: string) => {
    const isChecked = selectedFields.indexOf(value) > -1;

    if (isChecked) {
      const filters = selectedFields.filter((field) => field !== value);
      setSelectedFields(filters);
      updateParentFilter(filters);
    } else {
      const filters = [...selectedFields, value];
      setSelectedFields(filters);
      updateParentFilter(filters);
    }
  };

  useEffect(() => {
    if (version) return;

    const isDefaultSelectedFieldsPresent = defaultSelectedFields && defaultSelectedFields?.length > 0;
    // Clear the selected fields on rendering based on shouldClearSelectedField unless the `defaultSelectedFields` is provided
    setSelectedFields(isDefaultSelectedFieldsPresent ? defaultSelectedFields : []);
    // Update the parent filter with the default selected fields, if present
    isDefaultSelectedFieldsPresent && updateParentFilter(defaultSelectedFields);
  }, [shouldClearSelectedField, defaultSelectedFields, version]);

  useEffect(() => {
    if (!version) return;

    if (!shouldClearSelectedField) return;
    setSelectedFields([]);
  }, [shouldClearSelectedField, version]);

  const appliedFiltersCount = selectedFields?.length;
  const dropdownMenuTrigger = (
    <Button iconAfter={<ChevronDownIcon label="Down icon" size="medium" />} testId="custom-filter-checkbox-trigger">
      {filterField}
      {appliedFiltersCount > 0 && (
        <span className="pl-4">
          <Badge appearance="primary">{appliedFiltersCount}</Badge>
        </span>
      )}
    </Button>
  );

  return (
    <div className="custom-filter-checkbox">
      <DropdownMenu
        key={filterField}
        testId={filterField.toLowerCase().replace(' ', '-') + '-dropdown-menu'}
        trigger={dropdownMenuTrigger}
      >
        <DropdownItemGroup>
          {filterableItems.map((item) => {
            return (
              <div key={item.key} className="dropdown-checkbox">
                <Checkbox
                  value={item.value}
                  label={item.key}
                  onChange={() => handleSelect(item.value)}
                  isChecked={selectedFields.indexOf(item.value) > -1}
                />
              </div>
            );
          })}
        </DropdownItemGroup>
      </DropdownMenu>
    </div>
  );
};

export default CustomFilterCheckbox;
